import React, { useEffect, useState, useRef } from "react";
import { Table, Button, Modal, Form, Row, Col, InputGroup, Card, Container, Badge, ProgressBar } from "react-bootstrap";
import { Icon } from "@iconify/react";
import axios from "axios";
import { API_BASE_URL } from "./config";
import { toast, ToastContainer } from "react-toastify";
import certificadoImg from "../img/modelo_certificado.png";

const CERTIFICATE_WIDTH = 2000;
const CERTIFICATE_HEIGHT = 1414;
const CERTIFICATE_RENDER_SCALE = 0.42;
const MARGIN_LEFT = 360;
const MARGIN_RIGHT = 160;

const CertificadosLayout = () => {
    const userType = Number(localStorage.getItem("userType"));
    const currentUserName = String(localStorage.getItem("userName") || "");
    const isIvanGestor = userType === 1 && currentUserName.toLowerCase().includes("ivan");
    const [alunos, setAlunos] = useState([]);
    const [escolas, setEscolas] = useState([]);
    const [selectedEscolaId, setSelectedEscolaId] = useState("all");
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedAluno, setSelectedAluno] = useState(null);
    const [selectedAlunoStatus, setSelectedAlunoStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [devPreview, setDevPreview] = useState(false);
    const [brokenAvatarById, setBrokenAvatarById] = useState({});
    
    // Dados do formulário
    const [formData, setFormData] = useState({
        dataConclusao: new Date().toISOString().split('T')[0],
        cargaHoraria: "100",
        curso: "Inglês"
    });

    const cursosDisponiveis = [
        "Inglês",
        "Espanhol",
        "Alemão",
        "Francês",
        "Russo"
    ];

    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");

    const certificateRef = useRef();

    useEffect(() => {
        if (userType === 1) {
            fetchAlunos();
            fetchEscolas();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchAlunos = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            // Filtrar apenas alunos (userType 5) e com nome válido
            const alunosFiltrados = response.data.filter(user => 
                user.cp_tipo_user === 5 && 
                user.cp_nome && 
                user.cp_nome.trim() !== "" &&
                user.cp_nome !== "null" &&
                user.cp_nome !== "undefined"
            );
            setAlunos(alunosFiltrados);
            setLoading(false);
        } catch (error) {
            console.error("Erro ao buscar alunos:", error);
            toast.error("Erro ao carregar lista de alunos.");
            setLoading(false);
        }
    };

    const fetchEscolas = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/escolas`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const ativas = Array.isArray(response.data)
                ? response.data.filter((e) => e.cp_ec_excluido === 0 || e.cp_ec_excluido === "0")
                : [];
            setEscolas(ativas);
        } catch (error) {
            console.error("Erro ao buscar escolas:", error);
            // silencioso para não poluir UI
        }
    };

    const fetchCertificadoStatus = async (targetUserId) => {
        try {
            setStatusLoading(true);
            const response = await axios.get(`${API_BASE_URL}/certificados/status`, {
                params: targetUserId ? { userId: targetUserId } : undefined,
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setSelectedAlunoStatus(response.data);
            if (response.data?.cursoNome) {
                setFormData((prev) => ({ ...prev, curso: response.data.cursoNome }));
            }
        } catch (error) {
            console.error("Erro ao buscar status do certificado:", error);
            toast.error("Erro ao verificar liberação do certificado.");
            setSelectedAlunoStatus(null);
        } finally {
            setStatusLoading(false);
            if (userType === 5) setLoading(false);
        }
    };

    const filteredAlunos = alunos
        .filter(aluno => selectedEscolaId === "all" || String(aluno.cp_escola_id) === String(selectedEscolaId))
        .filter(aluno => 
            aluno.cp_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (aluno.cp_email && aluno.cp_email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .sort((a, b) => {
            if (sortOrder === 'asc') return a.cp_nome.localeCompare(b.cp_nome);
            return b.cp_nome.localeCompare(a.cp_nome);
        });

    const handleOpenModal = (aluno) => {
        setSelectedAluno(aluno);
        setSelectedAlunoStatus(null);
        setShowModal(true);
        setDevPreview(false);
        fetchCertificadoStatus(aluno.cp_id);
    };

    const handlePrint = () => {
        if (selectedAlunoStatus && selectedAlunoStatus.eligible === false) {
            toast.error("Certificado ainda não liberado para este aluno.");
            return;
        }
        window.print();
    };

    // Estilos para impressão (A4 Paisagem)
    const printStyles = `
        @media print {
            body * {
                visibility: hidden;
            }
            #certificate-print-area, #certificate-print-area * {
                visibility: visible;
            }
            #certificate-print-area {
                position: fixed;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                display: flex !important;
                justify-content: center;
                align-items: center;
                background-color: white;
                z-index: 9999;
            }
            /* Forçar background graphics */
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            /* Ocultar modal e outros elementos */
            .modal, .fade { display: none !important; }
        }
    `;

    // Estilos do certificado
    const certificateStyle = {
        width: `${CERTIFICATE_WIDTH}px`,
        height: `${CERTIFICATE_HEIGHT}px`,
        position: "relative",
        color: "var(--text-primary-light)",
        fontFamily: "'Arial', sans-serif",
        overflow: "hidden",
        boxShadow: "0 4px 15px rgba(0,0,0,0.2)", // Sombra melhorada
        backgroundColor: "var(--base)"
    };

    // Estilo para a versão de impressão (sem sombra, margem zero)
    const certificatePrintStyle = {
        ...certificateStyle,
        boxShadow: "none",
        width: `${CERTIFICATE_WIDTH}px`,
        height: `${CERTIFICATE_HEIGHT}px`,
        margin: "0 auto",
        pageBreakAfter: "always",
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
        backgroundColor: "#fff",
        color: "#000"
    };

    // Estilo da Imagem de Fundo
    const backgroundStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "contain",
        zIndex: 0
    };

    // Wrapper de pré-visualização centralizado (mantém layout fixo e cabendo no modal)
    const previewWrapperStyle = {
        width: `${Math.round(CERTIFICATE_WIDTH * CERTIFICATE_RENDER_SCALE)}px`,
        height: `${Math.round(CERTIFICATE_HEIGHT * CERTIFICATE_RENDER_SCALE)}px`,
        position: "relative",
        overflow: "hidden",
        borderRadius: "12px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        backgroundColor: "#fff",
        margin: "0 auto"
    };

    // Conteúdo escalonado dentro do wrapper
    const previewScaledInner = {
        width: `${CERTIFICATE_WIDTH}px`,
        height: `${CERTIFICATE_HEIGHT}px`,
        transform: `scale(${CERTIFICATE_RENDER_SCALE})`,
        transformOrigin: "top left",
        position: "absolute",
        top: 0,
        left: 0
    };

    // Posições (ajustáveis conforme a imagem)
    const nameStyle = {
        position: "absolute",
        top: "570px",
        left: `${MARGIN_LEFT}px`,
        right: `${MARGIN_RIGHT}px`,
        fontSize: "80px",
        fontWeight: "bold",
        textAlign: "center",
        textTransform: "uppercase",
        letterSpacing: "2px",
        fontFamily: "'Times New Roman', serif",
        color: "#0b4a6f",
        zIndex: 1
    };

    // dataStyle e hoursStyle removidos conforme orientação (não exibir data/horas)

    const textStyle = {
        position: "absolute",
        top: "750px",
        left: `${MARGIN_LEFT}px`,
        right: `${MARGIN_RIGHT}px`,
        fontSize: "34px",
        textAlign: "center",
        zIndex: 1,
        lineHeight: "1.5",
        fontFamily: "'Times New Roman', serif",
        color: "#111827"
    };

    const getProfileImage = (photo) => {
        if (!photo || photo === "null" || photo === "undefined") {
            return "assets/images/user.png";
        }
        // Se já for URL completa
        if (photo.startsWith("http://") || photo.startsWith("https://")) {
            return photo;
        }
        // Remove barra inicial duplicada se houver e corrige barras invertidas
        const cleanPhoto = photo.replace(/\\/g, '/').replace(/^\/+/, '');
        return `${API_BASE_URL}/${cleanPhoto}`;
    };

    const hasValidProfilePhoto = (photo) => {
        if (!photo || photo === "null" || photo === "undefined") return false;
        const trimmed = String(photo).trim();
        if (!trimmed || trimmed === "null" || trimmed === "undefined") return false;
        return true;
    };

    const getInitials = (name) => {
        const safe = String(name || "").trim();
        if (!safe) return "?";
        const parts = safe.split(/\s+/).filter(Boolean);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const getAvatarColor = (name) => {
        const str = String(name || "").trim().toLowerCase();
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
        }
        const hue = hash % 360;
        return `hsl(${hue} 65% 45%)`;
    };

    if (userType !== 1) {
        return (
            <Container fluid className="p-0">
                <Card className="shadow-sm border-0 radius-16 overflow-hidden bg-base">
                    <Card.Header className="bg-base border-bottom p-4">
                        <h5 className="mb-1 fw-bold text-primary-light">Certificados</h5>
                        <p className="text-muted mb-0 small">Acesso restrito ao gestor.</p>
                    </Card.Header>
                </Card>
            </Container>
        );
    }

    if (userType === 5) {
        const alunoNome = localStorage.getItem("userName") || "Aluno";
        const cursoNome = selectedAlunoStatus?.cursoNome || "Curso";
        const eligible = Boolean(selectedAlunoStatus?.eligible);
        const mediaCurso = selectedAlunoStatus?.mediaCurso;
        const audioPercent = selectedAlunoStatus?.audioPercent ?? 0;
        const requisitos = selectedAlunoStatus?.requisitos || { mediaMinima: 7, audioMinimoPercent: 70 };

        return (
            <Container fluid className="p-0">
                <style>{printStyles}</style>

                <Card className="shadow-sm border-0 radius-16 overflow-hidden bg-base">
                    <Card.Header className="bg-base border-bottom p-4">
                        <h5 className="mb-1 fw-bold text-primary-light">Meus Certificados</h5>
                        <p className="text-muted mb-0 small">O certificado libera automaticamente ao atingir os requisitos do curso.</p>
                    </Card.Header>

                    <Card.Body className="p-4">
                        {loading || statusLoading ? (
                            <div className="text-center py-5 text-muted">Verificando requisitos...</div>
                        ) : eligible ? (
                            <div className="d-flex flex-column align-items-center">
                                <div className="d-flex justify-content-between align-items-center w-100 mb-3">
                                    <div className="d-flex flex-column">
                                        <span className="fw-semibold text-primary-light">{cursoNome}</span>
                                        <span className="text-muted small">
                                            Média: {mediaCurso ?? "-"} | Áudios: {audioPercent}%
                                        </span>
                                    </div>
                                    <Button variant="success" onClick={handlePrint} className="fw-bold shadow-sm">
                                        <Icon icon="ph:printer-fill" className="me-2" /> Imprimir Certificado
                                    </Button>
                                </div>

                                <div style={previewWrapperStyle}>
                                    <div style={previewScaledInner}>
                                        <div style={certificateStyle}>
                                            <img src={certificadoImg} alt="Fundo Certificado" style={backgroundStyle} />
                                            <div style={nameStyle}>{alunoNome}</div>
                                            <div style={textStyle}>
                                                por ter concluído com êxito o curso <strong>{cursoNome}</strong> do programa de formação,<br />
                                                com carga horária total de <strong>{formData.cargaHoraria}</strong> horas.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mx-auto" style={{ maxWidth: 720 }}>
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="ph:lock-key" className="text-warning fs-4" />
                                        <span className="fw-bold text-primary-light">Nenhum certificado liberado ainda</span>
                                    </div>
                                    <Badge bg="secondary">Bloqueado</Badge>
                                </div>

                                <Card className="border-0 shadow-sm bg-neutral-50 mb-3">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-primary-light fw-medium">Média do curso</span>
                                            <span className="text-muted">
                                                {mediaCurso ?? 0} / {requisitos.mediaMinima}
                                            </span>
                                        </div>
                                        <ProgressBar
                                            now={Math.min(100, Math.round(((mediaCurso ?? 0) / requisitos.mediaMinima) * 100))}
                                            variant={(selectedAlunoStatus?.checks?.mediaOk ? "success" : "warning")}
                                        />
                                    </Card.Body>
                                </Card>

                                <Card className="border-0 shadow-sm bg-neutral-50">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-primary-light fw-medium">Áudios ouvidos</span>
                                            <span className="text-muted">
                                                {audioPercent}% / {requisitos.audioMinimoPercent}%
                                            </span>
                                        </div>
                                        <ProgressBar
                                            now={Math.min(100, audioPercent)}
                                            variant={(selectedAlunoStatus?.checks?.audioOk ? "success" : "warning")}
                                        />
                                    </Card.Body>
                                </Card>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {eligible && (
                    <div id="certificate-print-area" className="d-none">
                        <div style={{ transform: `scale(${CERTIFICATE_RENDER_SCALE})`, transformOrigin: "top left" }}>
                            <div style={certificatePrintStyle}>
                                <img src={certificadoImg} alt="Fundo Certificado" style={backgroundStyle} />
                                <div style={nameStyle}>{alunoNome}</div>
                                <div style={textStyle}>
                                    por ter concluído com êxito o curso <strong>{cursoNome}</strong> do programa de formação,<br />
                                    com carga horária total de <strong>{formData.cargaHoraria}</strong> horas.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer />
            </Container>
        );
    }

    return (
        <Container fluid className="p-0">
            <style>{printStyles}</style>
            
            <Card className="shadow-sm border-0 radius-16 overflow-hidden bg-base">
                <Card.Header className="bg-base border-bottom p-4">
                    <Row className="align-items-center g-3">
                        <Col md={6}>
                            <h5 className="mb-1 fw-bold text-primary-light">Emissão de Certificados</h5>
                            <p className="text-muted mb-0 small">Gerencie e emita certificados para seus alunos.</p>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex gap-2 justify-content-md-end">
                                {userType === 1 && (
                                    <Form.Select
                                        value={selectedEscolaId}
                                        onChange={(e) => setSelectedEscolaId(e.target.value)}
                                        className="w-auto bg-neutral-50 text-primary-light"
                                        style={{ minWidth: '220px' }}
                                        title="Filtrar por escola"
                                    >
                                        <option value="all">Todas as escolas</option>
                                        {escolas.map((esc) => (
                                            <option key={esc.cp_ec_id} value={esc.cp_ec_id}>
                                                {esc.cp_ec_nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                                <InputGroup className="w-auto flex-grow-1" style={{ maxWidth: '300px' }}>
                                    <InputGroup.Text className="bg-neutral-50 border-end-0">
                                        <Icon icon="ph:magnifying-glass" className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Buscar aluno..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="bg-neutral-50 border-start-0 ps-0 focus-ring-none text-primary-light"
                                    />
                                </InputGroup>
                                <Form.Select 
                                    value={sortOrder} 
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    className="w-auto bg-neutral-50 text-primary-light"
                                    style={{ minWidth: '140px' }}
                                >
                                    <option value="asc">A-Z</option>
                                    <option value="desc">Z-A</option>
                                </Form.Select>
                            </div>
                        </Col>
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-4">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 table-nowrap">
                            <thead className="bg-neutral-50 text-uppercase text-muted small">
                                <tr>
                                    <th className="ps-3 py-3 border-0">Aluno</th>
                                    <th className="py-3 border-0">Contato</th>
                                    <th className="pe-3 py-3 border-0 text-end">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="3" className="text-center py-5 text-muted">Carregando lista de alunos...</td></tr>
                                ) : filteredAlunos.length === 0 ? (
                                    <tr><td colSpan="3" className="text-center py-5 text-muted">Nenhum aluno encontrado.</td></tr>
                                ) : (
                                    filteredAlunos.map((aluno) => (
                                        <tr key={aluno.cp_id} className="border-bottom-hover">
                                            <td className="ps-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="position-relative">
                                                        {hasValidProfilePhoto(aluno.cp_foto_perfil) && !brokenAvatarById[aluno.cp_id] ? (
                                                            <img
                                                                src={getProfileImage(aluno.cp_foto_perfil)}
                                                                alt={aluno.cp_nome || ""}
                                                                className="rounded-circle object-fit-cover shadow-sm border border-white"
                                                                style={{ width: "48px", height: "48px" }}
                                                                onError={() => setBrokenAvatarById((prev) => ({ ...prev, [aluno.cp_id]: true }))}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle shadow-sm border border-white d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: "48px",
                                                                    height: "48px",
                                                                    backgroundColor: getAvatarColor(aluno.cp_nome),
                                                                    color: "#fff",
                                                                    fontWeight: 800,
                                                                    fontSize: "14px",
                                                                    letterSpacing: "0.5px",
                                                                    userSelect: "none"
                                                                }}
                                                                aria-label={aluno.cp_nome || ""}
                                                                title={aluno.cp_nome || ""}
                                                            >
                                                                {getInitials(aluno.cp_nome)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold text-primary-light">{aluno.cp_nome}</h6>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="text-primary-light">{aluno.cp_email}</span>
                                                    <span className="text-muted small">
                                                        {aluno.cp_telefone || aluno.cp_whatsapp || ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="pe-3 text-end">
                                                <Button 
                                                    variant="primary" 
                                                    size="sm" 
                                                    onClick={() => handleOpenModal(aluno)}
                                                    className="btn-icon-text rounded-pill px-3 py-2 fw-medium shadow-sm hover-translate"
                                                >
                                                    <Icon icon="ph:certificate-bold" className="me-2 fs-5" />
                                                    Emitir Certificado
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
                <Card.Footer className="bg-base border-top p-3 text-muted small text-center">
                    Mostrando {filteredAlunos.length} de {alunos.length} alunos
                </Card.Footer>
            </Card>

            {/* Modal de Certificado */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered contentClassName="border-0 shadow-lg radius-16">
                <Modal.Header closeButton className="border-bottom bg-neutral-50 px-4 py-3">
                    <Modal.Title className="fw-bold h5 mb-0 text-primary-light">Configurar Certificado</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="row g-0">
                        {/* Coluna de Configuração */}
                        <div className="col-lg-3 border-end bg-neutral-50 p-3">
                            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2 text-primary-light">
                                <Icon icon="ph:sliders-horizontal" /> Opções
                            </h6>
                            
                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-semibold text-muted">Aluno Selecionado</Form.Label>
                                <div className="p-2 bg-base border rounded mb-3 d-flex align-items-center gap-2">
                                    <Icon icon="ph:student" className="text-primary" />
                                    <span className="fw-medium text-truncate text-primary-light">{selectedAluno?.cp_nome}</span>
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-semibold text-muted">Curso Concluído</Form.Label>
                                {selectedAlunoStatus?.cursoNome ? (
                                    <Form.Control
                                        value={selectedAlunoStatus.cursoNome}
                                        readOnly
                                        className="form-control-lg fs-6 shadow-sm border-0 bg-base text-primary-light"
                                    />
                                ) : (
                                    <Form.Select
                                        value={formData.curso}
                                        onChange={(e) => setFormData({ ...formData, curso: e.target.value })}
                                        className="form-select-lg fs-6 shadow-sm border-0 bg-base text-primary-light"
                                    >
                                        {cursosDisponiveis.map(curso => (
                                            <option key={curso} value={curso}>{curso}</option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-semibold text-muted">Data de Conclusão</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    value={formData.dataConclusao} 
                                    onChange={(e) => setFormData({...formData, dataConclusao: e.target.value})}
                                    className="form-control-lg fs-6 shadow-sm border-0 bg-base text-primary-light"
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="small fw-semibold text-muted">Carga Horária (Horas)</Form.Label>
                                <InputGroup className="shadow-sm">
                                    <Form.Control 
                                        type="number" 
                                        value={formData.cargaHoraria} 
                                        onChange={(e) => setFormData({...formData, cargaHoraria: e.target.value})}
                                        className="form-control-lg fs-6 border-0 bg-base text-primary-light"
                                        placeholder="Ex: 100"
                                    />
                                    <InputGroup.Text className="bg-base border-0 text-muted">h</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>

                            <div className="d-grid gap-2">
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={handlePrint}
                                    className="fw-bold shadow-sm"
                                    disabled={selectedAlunoStatus?.eligible === false || statusLoading}
                                >
                                    <Icon icon="ph:printer-fill" className="me-2" /> Imprimir
                                </Button>
                                {isIvanGestor ? (
                                    <Button
                                        variant="warning"
                                        size="lg"
                                        onClick={() => setDevPreview((v) => !v)}
                                        className="fw-bold shadow-sm"
                                        disabled={statusLoading || !selectedAluno?.cp_id}
                                    >
                                        <Icon icon="ph:eye" className="me-2" /> {devPreview ? "Ocultar Visualização" : "Visualizar Certificado"}
                                    </Button>
                                ) : null}
                                <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </Button>
                            </div>
                        </div>

                        {/* Coluna de Visualização */}
                        <div className="col-lg-9 bg-neutral-100 p-4 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '500px' }}>
                            <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                                <Icon icon="ph:eye" />
                                <span className="small fw-medium text-uppercase ls-1">Pré-visualização</span>
                            </div>

                            {statusLoading ? (
                                <div className="text-center py-5 text-muted">Verificando requisitos...</div>
                            ) : (selectedAlunoStatus?.eligible || devPreview) ? (
                                <>
                                    {selectedAlunoStatus?.eligible ? (
                                        <Badge bg="success" className="mb-3">Liberado</Badge>
                                    ) : (
                                        <Badge bg="warning" className="mb-3 text-dark">Visualização (Dev)</Badge>
                                    )}

                                    <div style={previewWrapperStyle}>
                                        <div style={previewScaledInner}>
                                            <div style={certificateStyle}>
                                                <img src={certificadoImg} alt="Fundo Certificado" style={backgroundStyle} />
                                                <div style={nameStyle}>{selectedAluno?.cp_nome}</div>
                                                <div style={textStyle}>
                                                    por ter concluído com êxito o curso <strong>{selectedAlunoStatus?.cursoNome || formData.curso}</strong> do programa de formação,<br />
                                                    com carga horária total de <strong>{formData.cargaHoraria}</strong> horas.
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="mt-3 text-muted small text-center max-w-500">
                                        <Icon icon="ph:info" className="me-1" />
                                        O layout acima é apenas uma visualização. O arquivo final será gerado em alta resolução (A4 Paisagem).
                                    </p>
                                </>
                            ) : (
                                <div className="mx-auto w-100" style={{ maxWidth: 720 }}>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="ph:lock-key" className="text-warning fs-4" />
                                            <span className="fw-bold text-primary-light">Certificado não liberado</span>
                                        </div>
                                        <Badge bg="secondary">Bloqueado</Badge>
                                    </div>

                                    <Card className="border-0 shadow-sm bg-neutral-50 mb-3">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-primary-light fw-medium">Média do curso</span>
                                                <span className="text-muted">
                                                    {selectedAlunoStatus?.mediaCurso ?? 0} / {selectedAlunoStatus?.requisitos?.mediaMinima ?? 7}
                                                </span>
                                            </div>
                                            <ProgressBar
                                                now={Math.min(100, Math.round((((selectedAlunoStatus?.mediaCurso ?? 0) / (selectedAlunoStatus?.requisitos?.mediaMinima ?? 7)) * 100)))}
                                                variant={(selectedAlunoStatus?.checks?.mediaOk ? "success" : "warning")}
                                            />
                                        </Card.Body>
                                    </Card>

                                    <Card className="border-0 shadow-sm bg-neutral-50">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-primary-light fw-medium">Áudios ouvidos</span>
                                                <span className="text-muted">
                                                    {selectedAlunoStatus?.audioPercent ?? 0}% / {selectedAlunoStatus?.requisitos?.audioMinimoPercent ?? 70}%
                                                </span>
                                            </div>
                                            <ProgressBar
                                                now={Math.min(100, selectedAlunoStatus?.audioPercent ?? 0)}
                                                variant={(selectedAlunoStatus?.checks?.audioOk ? "success" : "warning")}
                                            />
                                        </Card.Body>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            
            {/* Área Oculta para Impressão */}
            <div id="certificate-print-area" className="d-none">
                <div style={{ transform: `scale(${CERTIFICATE_RENDER_SCALE})`, transformOrigin: "top left" }}>
                    <div style={certificatePrintStyle}>
                        <img src={certificadoImg} alt="Fundo Certificado" style={backgroundStyle} />
                        <div style={nameStyle}>{selectedAluno?.cp_nome}</div>
                        <div style={textStyle}>
                            por ter concluído com êxito o curso <strong>{selectedAlunoStatus?.cursoNome || formData.curso}</strong> do programa de formação,<br />
                            com carga horária total de <strong>{formData.cargaHoraria}</strong> horas.
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer />
        </Container>
    );
};

export default CertificadosLayout;
